import { create } from 'zustand';
import type { User } from '../types';
import type { RegisterFormData, LoginFormData } from '../types/user';
import {
  registerUser,
  loginUser,
  loginWithGoogle,
  signOut,
  subscribeToAuthState,
  updateUserAvatar,
  updateUserDisplayName,
} from '../services/firebase/auth';
import { initializeFirebase } from '../services/firebase/config';

interface AuthStore {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  initialize: () => void;
  register: (data: RegisterFormData) => Promise<void>;
  login: (data: LoginFormData) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  updateAvatar: (avatarUrl: string) => Promise<void>;
  updateDisplayName: (displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

/**
 * Auth state management store.
 */
export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  isLoading: true,
  error: null,

  initialize: () => {
    initializeFirebase();

    subscribeToAuthState((user) => {
      set({ user, isLoading: false });
    });
  },

  register: async (data: RegisterFormData) => {
    set({ isLoading: true, error: null });

    try {
      const user = await registerUser(data);
      set({ user, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Registration failed';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  login: async (data: LoginFormData) => {
    set({ isLoading: true, error: null });

    try {
      const user = await loginUser(data);
      set({ user, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  loginWithGoogle: async () => {
    set({ isLoading: true, error: null });

    try {
      const user = await loginWithGoogle();
      set({ user, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Google sign-in failed';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateAvatar: async (avatarUrl: string) => {
    set({ isLoading: true, error: null });

    try {
      const user = await updateUserAvatar(avatarUrl);
      set({ user, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Avatar update failed';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateDisplayName: async (displayName: string) => {
    set({ isLoading: true, error: null });

    try {
      const user = await updateUserDisplayName(displayName);
      set({ user, isLoading: false });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Display name update failed';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true, error: null });

    try {
      await signOut();
      set({ user: null, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Logout failed';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));

// Initialize auth on module load
useAuthStore.getState().initialize();
